import {
    useIsMutating,
    useMutation,
    useQueryClient,
} from "@tanstack/react-query";
import axios from "axios";

import { getApiErrorMessage } from "../api.types";
import {
    createOrganizationClauseGuidance,
    deleteOrganizationClauseGuidance,
    updateOrganizationClauseGuidance,
} from "./clauses";
import type { IClauseGuidanceResponse } from "./clauses.types";
import type { ClauseGuidanceWriteDecision } from "./decideClauseGuidanceSave";
import { clauseGuidanceQueryOptions } from "./useClauseGuidance";

export class ClauseGuidanceWriteError extends Error {
    readonly status: number | undefined;
    readonly detail: string | null;
    readonly cause: unknown;

    constructor(cause: unknown) {
        super("Guidance could not be saved");
        this.name = "ClauseGuidanceWriteError";
        this.status = axios.isAxiosError(cause)
            ? cause.response?.status
            : undefined;
        this.detail = axios.isAxiosError(cause)
            ? getApiErrorMessage(cause, "") || null
            : null;
        this.cause = cause;
    }
}

export class ClauseGuidanceWriteConflictError extends ClauseGuidanceWriteError {
    readonly decision: ClauseGuidanceWriteDecision;

    constructor(decision: ClauseGuidanceWriteDecision, cause: unknown) {
        super(cause);
        this.name = "ClauseGuidanceWriteConflictError";
        this.decision = decision;
    }
}

export class ClauseGuidanceRefreshUnconfirmedError extends Error {
    readonly confirmedWrite: ClauseGuidanceWriteDecision;
    readonly cause: unknown;

    constructor(confirmedWrite: ClauseGuidanceWriteDecision, cause: unknown) {
        super("Guidance was saved, but could not be refreshed");
        this.name = "ClauseGuidanceRefreshUnconfirmedError";
        this.confirmedWrite = confirmedWrite;
        this.cause = cause;
    }
}

export class ClauseGuidanceRefreshDivergentError extends Error {
    readonly confirmedWrite: ClauseGuidanceWriteDecision;
    readonly observedGuidance: IClauseGuidanceResponse;

    constructor(
        confirmedWrite: ClauseGuidanceWriteDecision,
        observedGuidance: IClauseGuidanceResponse
    ) {
        super("Guidance changed after the write");
        this.name = "ClauseGuidanceRefreshDivergentError";
        this.confirmedWrite = confirmedWrite;
        this.observedGuidance = observedGuidance;
    }
}

const clauseGuidanceMutationKey = (clauseId: number) =>
    ["clause-guidance", clauseId] as const;

export const useIsClauseGuidancePending = (clauseId: number): boolean =>
    useIsMutating({
        mutationKey: clauseGuidanceMutationKey(clauseId),
        exact: true,
    }) > 0;

export const useSaveClauseGuidance = (clauseId: number) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: clauseGuidanceMutationKey(clauseId),
        scope: { id: `clause-guidance:${clauseId}` },
        mutationFn: async (decision: ClauseGuidanceWriteDecision) => {
            try {
                if (decision.kind === "create") {
                    await createOrganizationClauseGuidance(clauseId, {
                        guidance: decision.guidance,
                    });
                } else if (decision.kind === "update") {
                    await updateOrganizationClauseGuidance(clauseId, {
                        guidance: decision.guidance,
                    });
                } else {
                    await deleteOrganizationClauseGuidance(clauseId);
                }
            } catch (cause) {
                if (
                    decision.kind !== "delete" ||
                    !axios.isAxiosError(cause) ||
                    cause.response?.status !== 404
                ) {
                    const error = new ClauseGuidanceWriteError(cause);
                    if (
                        (decision.kind === "create" &&
                            error.status === 422 &&
                            error.detail ===
                                "Guidance for this clause already exists for this organization") ||
                        (decision.kind === "update" && error.status === 404)
                    ) {
                        throw new ClauseGuidanceWriteConflictError(
                            decision,
                            cause
                        );
                    }
                    throw error;
                }
            }

            const guidanceQuery = clauseGuidanceQueryOptions(clauseId);
            await queryClient.cancelQueries({
                queryKey: guidanceQuery.queryKey,
            });
            let confirmation:
                | { status: "fulfilled"; value: IClauseGuidanceResponse }
                | { status: "rejected"; reason: unknown };
            try {
                confirmation = {
                    status: "fulfilled",
                    value: await queryClient.fetchQuery({
                        ...guidanceQuery,
                        staleTime: 0,
                    }),
                };
            } catch (reason) {
                confirmation = { status: "rejected", reason };
            }
            const queryKeys: ReadonlyArray<readonly unknown[]> = [
                ["frontend-organization-clause-guidance", clauseId],
                ["frontend-chart"],
                ["frontend-scope"],
                ["frontend-organization-recent-scopes"],
                ["scope-topics-with-clauses"],
            ];
            await Promise.allSettled(
                queryKeys.map((queryKey) =>
                    queryClient.invalidateQueries({ queryKey })
                )
            );
            if (confirmation.status === "rejected") {
                throw new ClauseGuidanceRefreshUnconfirmedError(
                    decision,
                    confirmation.reason
                );
            }
            const confirmedOwn = confirmation.value.own;
            const matches =
                decision.kind === "delete"
                    ? confirmedOwn === null
                    : confirmedOwn?.guidance === decision.guidance;
            if (!matches) {
                throw new ClauseGuidanceRefreshDivergentError(
                    decision,
                    confirmation.value
                );
            }
            return decision;
        },
    });
};
